import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { Alert } from "@mui/material";
import ConfigData from "../config/config.json";
import { Copyright } from "./SignIn";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { useLocation } from "react-router";

const webserver = ConfigData.WEBSERVER;

const regex =
  /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[ !"§#$%&'()*+,\-./:;<=>?@[\]^_`{|}~])/gm;

export default function ForgotPassword(props) {
  const location = useLocation();
  const [showPassword, setShowPassword] = React.useState(false);
  const [feedback, setFeedback] = React.useState("");
  const [eMailSent, setEMailSent] = React.useState(false);
  const [eMail, setEMail] = React.useState(() => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get("email") || "";
  });
  const [token, setToken] = React.useState("");
  const [tokenSubmitted, setTokenSubmitted] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = React.useState("");
  const [errors, setErrors] = React.useState({
    minLengthError: "",
    regexError: "",
    confirmationError: "",
    userExistsError: "",
  });
  const [saveDisabled, setSaveDisabled] = React.useState(true);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  React.useEffect(() => {
    const newErrors = {
      minLengthError: "",
      regexError: "",
      confirmationError: "",
      userExistsError: "",
    };

    if (newPassword.length < 8 && newPassword.length > 0) {
      newErrors.minLengthError = "Minimale Passwortlänge: 8 Zeichen.";
    } else {
      newErrors.minLengthError = "";
    }

    if (!regex.test(newPassword) && newPassword.length > 0) {
      newErrors.regexError =
        "Minimale Passwort-Komplexität: Mindestens je ein Groß-, Kleinbuchstabe, Sonderzeichen und eine Zahl.";
    } else {
      newErrors.regexError = "";
    }

    if (newPassword !== newPasswordConfirm) {
      newErrors.confirmationError =
        '"Passwort" und "Passwort bestätigen" müssen übereinstimmen.';
    } else {
      newErrors.confirmationError = "";
    }
    setErrors(newErrors);
  }, [newPassword, newPasswordConfirm]);

  React.useEffect(() => {
    if (
      !(
        newPassword.length < 8 ||
        !regex.test(newPassword) ||
        newPassword !== newPasswordConfirm
      )
    ) {
      setSaveDisabled(false);
    } else {
      setSaveDisabled(true);
    }
  }, [newPassword, newPasswordConfirm]);

  const handleEMailSubmit = (event) => {
    event.preventDefault();

    // POST request with a x-www-form-urlencoded body to be received by the java spring /login endpoint
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: eMail,
      credentials: "include",
    };

    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/user/forgotPassword",
      requestOptions
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Ungültige Angaben!");
        }
        return response;
      })
      .then((response) => response.text())
      .then((data) => {
        setFeedback(data);
        setEMailSent(true);
      })
      .catch((error) => {
        setFeedback(error.message);
        document.getElementById("eMail").value = "";
        document.getElementById("eMail").focus();
      });
  };

  const handleSubmitToken = (event) => {
    event.preventDefault();

    // POST request with a x-www-form-urlencoded body to be received by the java spring /login endpoint
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: token,
      credentials: "include",
    };

    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/user/verifyToken",
      requestOptions
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Ungültige Angaben!");
        }
        return response;
      })
      .then((response) => response.text())
      .then((data) => {
        if (data !== "true") {
          document.getElementById("token").focus();
          setFeedback(
            "Der Bestätigungscode wurde nicht korrekt eingegeben oder ist abgelaufen. Starten Sie den Prozess ggf. neu!"
          );
        } else {
          setTokenSubmitted(true);
          setFeedback("");
        }
      })
      .catch((error) => {
        setFeedback(error.message);
        document.getElementById("token").value = "";
        document.getElementById("token").focus();
      });
  };

  const handleNewPasswordSubmit = (event) => {
    event.preventDefault();

    // POST request with a x-www-form-urlencoded body to be received by the java spring /login endpoint
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: newPassword,
      credentials: "include",
    };

    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/user/changePassword/" +
        token,
      requestOptions
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Ungültige Angaben!");
        }
        return response;
      })
      .then((response) => response.text())
      .then((data) => {
        setFeedback(data);
      })
      .catch((error) => {
        setFeedback(error.message);
      });
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Passwort wiederherstellen
        </Typography>
        <Box component="form" sx={{ mt: 1 }}>
          {eMailSent ? (
            tokenSubmitted ? (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="newPassword"
                  label="Neues Passwort"
                  name="newPassword"
                  autoFocus
                  value={newPassword}
                  type={showPassword ? "text" : "password"}
                  onChange={(event) => setNewPassword(event.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="newPasswordConfirm"
                  label="Passwort wiederholen"
                  name="newPasswordConfirm"
                  autoFocus
                  value={newPasswordConfirm}
                  type={showPassword ? "text" : "password"}
                  onChange={(event) =>
                    setNewPasswordConfirm(event.target.value)
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            ) : (
              <TextField
                margin="normal"
                required
                fullWidth
                id="token"
                label="Bestätigungscode"
                name="token"
                autoFocus
                value={token}
                onChange={(event) => setToken(event.target.value)}
              />
            )
          ) : (
            <TextField
              margin="normal"
              required
              fullWidth
              id="eMail"
              label="E-Mail"
              name="eMail"
              autoComplete="email"
              autoFocus
              value={eMail}
              onChange={(event) => {
                setEMail(event.target.value);
              }}
            />
          )}
          {feedback && (
            <Alert sx={{ mt: 3 }} variant="outlined" severity="info">
              {feedback}
            </Alert>
          )}
          {(errors.minLengthError ||
            errors.regexError ||
            errors.confirmationError ||
            errors.userExistsError) && (
            <Alert sx={{ mt: 3 }} variant="outlined" severity="error">
              <ul style={{ margin: 0 }}>
                {errors.minLengthError && (
                  <li key="minLengthError">{errors.minLengthError}</li>
                )}
                {errors.regexError && (
                  <li key="regexError">{errors.regexError}</li>
                )}
                {errors.confirmationError && (
                  <li key="confirmationError">{errors.confirmationError}</li>
                )}
                {errors.userExistsError && (
                  <li key="userExistsError">{errors.userExistsError}</li>
                )}
              </ul>
            </Alert>
          )}
          {eMailSent ? (
            tokenSubmitted ? (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 2 }}
                disabled={saveDisabled}
                onClick={handleNewPasswordSubmit}
              >
                Passwort festlegen
              </Button>
            ) : (
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 2 }}
                onClick={handleSubmitToken}
              >
                Identität bestätigen
              </Button>
            )
          ) : (
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 2, mb: 2 }}
              onClick={handleEMailSubmit}
            >
              Passwort ändern
            </Button>
          )}
          <Box sx={{ display: "flex" }}>
            <Link sx={{ marginLeft: "auto" }} href="/" variant="body2">
              Zurück zum Login
            </Link>
          </Box>
        </Box>
      </Box>
      <Copyright sx={{ mt: 8, mb: 4 }} />
    </Container>
  );
}
